import axios, {
  AxiosError,
  AxiosRequestConfig,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig,
} from "axios";

import { HTTP_STATUS } from "@/constants/httpStatus";
import { paths } from "@/routes/paths";

import {
  clearAuthData,
  isTokenExpiredOrExpiringSoon,
  isTokenValid,
  refreshAccessToken,
} from "./tokenRefresh";

const BUFFER_SECONDS = 300; // 5 minuts buffer

// ----------------------------------------------------------------------

const commonAxiosOptions: CreateAxiosDefaults = {
  baseURL: `${import.meta.env.VITE_BASE_URL}`,
  timeout: 30000,
};

export const uninterceptedAxiosInstance = axios.create(commonAxiosOptions);

const axiosInstance = axios.create(commonAxiosOptions);
const STORAGE_KEY = "access_token";
const AUTH_TOKEN_ENDPOINT = "/auth/token/";
const USERS_TOKEN_ENDPOINT = "/users/token/";
const TOKEN_REFRESH_ENDPOINT = "/users/token/refresh/";
const ONE_ID_AUTH_ENDPOINT = "/users/auth/one-id/";

/**
 * Gets the current language from localStorage (i18next).
 * Defaults to "uz" if not found.
 */
const getCurrentLanguage = (): string => {
  const i18nLanguage = localStorage.getItem("i18nextLng");
  if (i18nLanguage && (i18nLanguage === "uz" || i18nLanguage === "en")) {
    return i18nLanguage;
  }
  return "uz";
};

const isAuthEndpoint = (url?: string): boolean =>
  Boolean(
    url?.includes(AUTH_TOKEN_ENDPOINT) ||
    url?.includes(TOKEN_REFRESH_ENDPOINT) ||
    url?.includes(USERS_TOKEN_ENDPOINT) ||
    url?.includes(ONE_ID_AUTH_ENDPOINT),
  );

const isLoginAttemptEndpoint = (url?: string): boolean =>
  Boolean(
    url?.includes(AUTH_TOKEN_ENDPOINT) ||
    url?.includes(USERS_TOKEN_ENDPOINT) ||
    url?.includes(ONE_ID_AUTH_ENDPOINT),
  );

// Add language header to unintercepted instance
uninterceptedAxiosInstance.interceptors.request.use(
  (config) => {
    const language = getCurrentLanguage();
    // eslint-disable-next-line no-param-reassign
    config.headers["Accept-Language"] = language;
    return config;
  },
  (error) => {
    throw error;
  },
);

axiosInstance.interceptors.request.use(
  async (config) => {
    // Skip token refresh check for auth endpoints
    if (!isAuthEndpoint(config.url)) {
      // Check if token is expired or expiring soon (within buffer time)
      const shouldRefresh = isTokenExpiredOrExpiringSoon(BUFFER_SECONDS);

      if (shouldRefresh) {
        try {
          // Proactively refresh the token before making the request
          const newAccessToken = await refreshAccessToken();

          if (newAccessToken) {
            // Update the token in config
            if (config.headers) {
              // eslint-disable-next-line no-param-reassign
              config.headers.Authorization = `Bearer ${newAccessToken}`;
            }
            const language = getCurrentLanguage();
            // eslint-disable-next-line no-param-reassign
            config.headers["Accept-Language"] = language;
            return config;
          }

          // If refresh failed (returned null), clear auth data and redirect to login
          clearAuthData();
          window.location.href = paths.auth.login;
          // Return config to satisfy TypeScript, even though we're redirecting
          return config;
        } catch (error) {
          // Refresh failed, clear auth data and redirect to login
          clearAuthData();
          window.location.href = paths.auth.login;
          // Return config to satisfy TypeScript, even though we're redirecting
          return config;
        }
      }
    }

    // Use existing token if it's still valid
    const token = localStorage.getItem(STORAGE_KEY);
    const language = getCurrentLanguage();

    if (token) {
      // Always attach token (even if invalid) so server can reject it properly
      // Invalid tokens will trigger 401, which the response interceptor will handle
      if (config.headers) {
        // eslint-disable-next-line no-param-reassign
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // eslint-disable-next-line no-param-reassign
    config.headers["Accept-Language"] = language;

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      retryAttempted?: boolean;
    };

    // Handle 401 Unauthorized errors - try to refresh token
    if (
      error.response &&
      error.response.status === HTTP_STATUS.UNAUTHORIZED &&
      !originalRequest.retryAttempted
    ) {
      // Let login endpoints handle 401 in page-level auth flow (no forced reload).
      if (isLoginAttemptEndpoint(originalRequest.url)) {
        return Promise.reject(error);
      }

      // If refresh endpoint itself returns 401, session is invalid.
      if (originalRequest.url?.includes(TOKEN_REFRESH_ENDPOINT)) {
        clearAuthData();
        window.location.href = paths.auth.login;
        return Promise.reject(error);
      }

      originalRequest.retryAttempted = true;

      try {
        const newAccessToken = await refreshAccessToken();

        if (newAccessToken && originalRequest.headers) {
          // Validate the new token before using it
          if (isTokenValid(newAccessToken)) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return await axiosInstance(originalRequest);
          }
          // New token is also invalid - clear and redirect
          clearAuthData();
          window.location.href = paths.auth.login;
          throw new Error("Refreshed token is invalid");
        }
        // Refresh returned null - clear and redirect
        clearAuthData();
        window.location.href = paths.auth.login;
        throw new Error("Token refresh failed");
      } catch (refreshError) {
        // Refresh failed, redirect to login
        clearAuthData();
        window.location.href = paths.auth.login;
        return Promise.reject(refreshError);
      }
    }

    // Handle 403 Forbidden errors
    if (error.response && error.response.status === HTTP_STATUS.FORBIDDEN) {
      if (isLoginAttemptEndpoint(originalRequest.url)) {
        return Promise.reject(error);
      }

      // Clear all authentication data
      clearAuthData();

      // Navigate to auth login page
      window.location.href = paths.auth.login;
    }

    throw error;
  },
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

// ----------------------------------------------------------------------

export const endpoints = {
  chat: "/api/chat",
  kanban: "/api/kanban",
  calendar: "/api/calendar",
  auth: {
    me: "/admin/me",
    login: "/auth/admin/signin",
    register: "/api/auth/register",
    oneId: "/users/auth/one-id/",
    token: "/users/token/",
    tokenRefresh: "/users/token/refresh/",
    profile: "/users/profile/",
  },
  mail: {
    list: "/api/mail/list",
    details: "/api/mail/details",
    labels: "/api/mail/labels",
  },
  post: {
    list: "/api/post/list",
    details: "/api/post/details",
    latest: "/api/post/latest",
    search: "/api/post/search",
  },
  product: {
    list: "/api/product/list",
    details: "/api/product/details",
    search: "/api/product/search",
  },
  citizens: {
    list: "/registry/citizens/",
    youthBalanceList: "/youth-balance/citizens/",
    detail: (id: string) => `/registry/citizens/${id}/`,
    educationInfo: (id: string) => `/registry/citizens/${id}/education-info/`,
    legalSocialInfo: (id: string) =>
      `/registry/citizens/${id}/legal-social-info/`,
    projects: (id: string) => `/registry/citizens/${id}/projects/`,
    employmentAndEntrepreneurship: (id: string) =>
      `/registry/citizens/${id}/employment-and-entrepreneurship/`,
    migrationInfo: (id: string) => `/registry/citizens/${id}/migration-info/`,
    documents: (id: string) => `/registry/citizens/${id}/documents/`,
    setEmploymentStatus: (uuid: string) =>
      `/registry/citizens/${uuid}/set-employment-status/`,
    advancedSearch: "/clickhouse/advanced-search/",
  },
  common: {
    regions: "/common/regions/",
    cities: "/common/cities/",
    makhallas: "/common/makhallas/",
  },
  internalMigrants: {
    list: "/registry/internal-migrants/",
    counts: "/registry/internal-migrants/counts/",
    table: "/registry/internal-migrants/table/",
  },
  integrations: {
    list: "/integrations/integration-systems/",
  },
  opportunities: {
    list: "/opportunity/opportunities/",
    detail: (id: string) => `/opportunity/opportunities/${id}/`,
  },
};
